import type { ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useFeatureFlagWithLoading } from '@/contexts/FeatureFlagContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import type { FeatureFlags } from '@/types/featureFlags';

interface FeatureGateProps {
  /**
   * The feature flag key to check.
   */
  flag: keyof FeatureFlags | string;

  /**
   * Content to render when the flag is enabled (or disabled if invert=true).
   */
  children: ReactNode;

  /**
   * Optional fallback content to render when the flag is disabled.
   * Defaults to null (renders nothing).
   */
  fallback?: ReactNode;

  /**
   * If true, inverts the logic - shows children when flag is OFF.
   * Defaults to false.
   */
  invert?: boolean;

  /**
   * If true, shows loading component while flags are being fetched.
   * Defaults to false (no loading state).
   */
  showLoading?: boolean;

  /**
   * Custom loading component to show while flags are being fetched.
   * Only used if showLoading is true.
   */
  loadingComponent?: ReactNode;
}

/**
 * Conditionally renders children based on feature flag.
 *
 * Usage:
 *   <FeatureGate flag="show_scoring_101">
 *     <StatsLink />
 *   </FeatureGate>
 *
 *   <FeatureGate flag="show_scoring_101" fallback={<Navigate to="/" />}>
 *     <StatsPage />
 *   </FeatureGate>
 *
 *   <FeatureGate flag="maintenance_mode" invert>
 *     <AppContent />
 *   </FeatureGate>
 */
export function FeatureGate({
  flag: flagKey,
  children,
  fallback = null,
  invert = false,
  showLoading = false,
  loadingComponent = null,
}: FeatureGateProps) {
  const { enabled, hasLoaded } = useFeatureFlagWithLoading(flagKey as string);
  const { isSignedIn } = useAuth();
  const { data: profile, isLoading: profileLoading } = useUserProfile();

  // Super admins bypass all feature flags - they see everything
  const isSuperAdmin = profile?.is_super_admin ?? false;

  // Never render a fallback (which may be a <Navigate> redirect) until flags
  // have been fetched at least once.  This prevents premature redirects on
  // page refresh regardless of isLoading timing or StrictMode race conditions.
  // Also wait for the user profile to load for signed-in users, so the
  // super-admin bypass can take effect before a redirect fires.
  if (!hasLoaded || (isSignedIn && profileLoading)) {
    if (showLoading) {
      return <>{loadingComponent}</>;
    }
    return null;
  }

  // Super admins always see children (unless invert mode is on for things like maintenance)
  if (isSuperAdmin && !invert) {
    return <>{children}</>;
  }

  // Determine if we should show children based on flag and invert setting
  const shouldShow = invert ? !enabled : enabled;

  return <>{shouldShow ? children : fallback}</>;
}
